/**
 * Switching a company's receivables source — Google Sheet to TallyPrime —
 * without losing a single follow-up, promise, note or rank.
 *
 * The book's accounts carry their history; the new source names the same
 * customers its own way (a Tally ledger GUID, where the sheet had a row label
 * or a customer code). Switching means binding each existing account to its
 * Tally ledger, once, with the owner looking. This module only *proposes*
 * those bindings; nothing is bound until the owner confirms
 * (docs/tally/09-UX-AND-SOURCE-SWITCHING.md).
 *
 * How sure each proposal is, in order:
 *
 *   linked           the account already carries the ledger's identity
 *                    (an earlier connection to the same company)        → strong
 *   gstin            the same GSTIN on both sides, unique on both    → strong
 *   name_and_amount  the same name once tidied, unique on both, and
 *                    the same balance (to the rupee, same side)       → strong
 *   name_only        the same tidied name, unique, but the balance
 *                    differs                                          → the owner decides
 *   contact          the same phone number or email, unique          → the owner decides
 *
 * What cannot be decided is reported as a **conflict** and never bound: a
 * GSTIN or a name shared by several accounts or ledgers, or one name with two
 * different GSTINs. The owner matches those by hand, or leaves them.
 *
 * A display name alone is never enough to bind silently: two customers can be
 * called "Sharma Traders", and a sheet's name is whatever someone typed. Only
 * a strong proposal is pre-selected for the owner; the rest are suggestions.
 * An account nothing matches is listed, never settled or deleted by the
 * switch; a ledger nothing matches becomes a new account.
 */
import type { BalanceType } from './customer.js';
import type { ExistingCustomer, ImportedRow } from './sheetImport.js';

export interface SwitchExisting extends Pick<ExistingCustomer, 'id' | 'company' | 'total' | 'totalType' | 'settledAt'> {
    /** The identity the account carries now (a sheet's key, or a Tally ledger's from an earlier connection). */
    sourceKey?: string | null;
    taxId?: string | null;
    contactNumber?: string | null;
    email?: string | null;
}

export type SwitchIncoming = Pick<ImportedRow, 'sourceKey' | 'company' | 'total' | 'totalType' | 'contact'>;

export type MatchBasis = 'linked' | 'gstin' | 'name_and_amount' | 'name_only' | 'contact';

/** Something the proposal will not decide: the owner must. */
export interface SwitchConflict {
    reason: 'shared_gstin' | 'shared_name' | 'gstin_differs';
    accountIds: string[];
    sourceKeys: string[];
    /** One sentence for the review screen. */
    detail: string;
}

export interface ProposedBinding {
    accountId: string;
    accountName: string;
    sourceKey: string;
    ledgerName: string;
    basis: MatchBasis;
    /** Strong proposals are pre-selected; suggestions need the owner to tick them. */
    strength: 'strong' | 'suggestion';
    /** What the owner is shown beside it: why, and anything that disagrees. */
    notes: string[];
}

export interface SwitchProposal {
    bindings: ProposedBinding[];
    /** Existing accounts with no Tally ledger proposed: the owner says what they are. */
    unmatchedAccounts: { accountId: string; accountName: string; total: number; totalType: BalanceType }[];
    /** Tally ledgers with no account proposed: they become new accounts. */
    newLedgers: { sourceKey: string; ledgerName: string; total: number; totalType: BalanceType }[];
    /** What must not be merged automatically. Its accounts and ledgers are also among the unmatched and the new. */
    conflicts: SwitchConflict[];
    counts: { accounts: number; ledgers: number; strong: number; suggestions: number; unmatchedAccounts: number; newLedgers: number; conflicts: number };
}

const GSTIN = /^[0-9]{2}[A-Z0-9]{10}[0-9A-Z]Z[0-9A-Z]$/;

/** A GSTIN, tidied, or null if it is not one. */
export function tidyGstin(value: string | null | undefined): string | null {
    const v = (value ?? '').toUpperCase().replace(/[\s-]/g, '');
    return GSTIN.test(v) ? v : null;
}

const SUFFIXES = /\b(m\/s|messrs|pvt|private|ltd|limited|llp|co|company|and|the|inc|corp|corporation|enterprises?)\b/g;

/** A name for comparison: case, punctuation, "M/s", "Pvt Ltd", "& Co" and spacing do not matter. */
export function tidyName(name: string): string {
    return name.normalize('NFKC').toLowerCase()
        .replace(/&/g, ' and ')
        .replace(SUFFIXES, ' ')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}

/** The last ten digits of a phone number, or null. */
export function tidyPhone(value: string | null | undefined): string | null {
    const digits = (value ?? '').replace(/\D/g, '');
    return digits.length >= 10 ? digits.slice(-10) : null;
}

const tidyEmail = (value: string | null | undefined): string | null => {
    const v = (value ?? '').trim().toLowerCase();
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? v : null;
};

/** Signed, in rupees: Dr positive. */
const signed = (total: number, type: BalanceType | undefined) => Math.round((type === 'Cr' ? -1 : 1) * total);

function uniqueIndex<T>(items: T[], key: (item: T) => string | null): Map<string, T> {
    const seen = new Map<string, T | null>();
    for (const item of items) {
        const k = key(item);
        if (!k) continue;
        seen.set(k, seen.has(k) ? null : item);
    }
    return new Map([...seen].filter((e): e is [string, T] => e[1] !== null));
}

export function proposeSourceSwitch(existing: SwitchExisting[], incoming: SwitchIncoming[]): SwitchProposal {
    const accounts = existing.filter(a => !a.settledAt || Math.abs(a.total) > 0);
    const ledgers = incoming.filter((l): l is SwitchIncoming & { sourceKey: string } => Boolean(l.sourceKey));
    const boundAccounts = new Set<string>();
    const boundLedgers = new Set<string>();
    const bindings: ProposedBinding[] = [];

    const bind = (a: SwitchExisting, l: SwitchIncoming & { sourceKey: string }, basis: MatchBasis, strength: ProposedBinding['strength'], notes: string[]) => {
        boundAccounts.add(a.id);
        boundLedgers.add(l.sourceKey);
        bindings.push({ accountId: a.id, accountName: a.company, sourceKey: l.sourceKey, ledgerName: l.company, basis, strength, notes });
    };
    const amountNote = (a: SwitchExisting, l: SwitchIncoming) => {
        const was = signed(a.total, a.totalType);
        const now = signed(l.total, l.totalType);
        return was === now ? null : `The book says ${Math.abs(was)} ${was < 0 ? 'Cr' : 'Dr'}; TallyPrime says ${Math.abs(now)} ${now < 0 ? 'Cr' : 'Dr'}.`;
    };
    const free = <T extends { id?: string; sourceKey?: string | null }>(list: T[], bound: Set<string>, key: (t: T) => string) => list.filter(t => !bound.has(key(t)));

    // A GSTIN held by several accounts, or several ledgers, makes any match involving it a
    // question for the owner: no name or contact proposal is made for it (it is a conflict).
    const gstinCount = <T>(items: T[], key: (t: T) => string | null) => {
        const out = new Map<string, number>();
        for (const item of items) { const k = key(item); if (k) out.set(k, (out.get(k) ?? 0) + 1); }
        return out;
    };
    const accountsWithGstin = gstinCount(accounts, a => tidyGstin(a.taxId));
    const ledgersWithGstin = gstinCount(ledgers, l => tidyGstin(l.contact.taxId));
    const gstinUnsafe = (a: SwitchExisting, l: SwitchIncoming) => {
        const ga = tidyGstin(a.taxId);
        const gl = tidyGstin(l.contact.taxId);
        return (ga !== null && gl !== null && ga !== gl)
            || (gl !== null && (accountsWithGstin.get(gl) ?? 0) > 1)
            || (ga !== null && (ledgersWithGstin.get(ga) ?? 0) > 1);
    };

    // 0. Already linked: the account carries this ledger's identity.
    {
        const byKey = new Map(ledgers.map(l => [l.sourceKey, l]));
        for (const a of accounts) {
            const l = a.sourceKey ? byKey.get(a.sourceKey) : undefined;
            if (!l || boundLedgers.has(l.sourceKey)) continue;
            const differs = amountNote(a, l);
            bind(a, l, 'linked', 'strong', ['Already linked to this TallyPrime ledger.', ...(differs ? [differs] : [])]);
        }
    }

    // 1. GSTIN, unique on both sides.
    {
        const byA = uniqueIndex(free(accounts, boundAccounts, a => a.id), a => tidyGstin(a.taxId));
        const byL = uniqueIndex(free(ledgers, boundLedgers, l => l.sourceKey), l => tidyGstin(l.contact.taxId));
        for (const [gstin, a] of byA) {
            const l = byL.get(gstin);
            if (!l) continue;
            const notes = [`Same GSTIN (${gstin}).`];
            if (tidyName(a.company) !== tidyName(l.company)) notes.push(`Named "${a.company}" in the book and "${l.company}" in TallyPrime.`);
            const differs = amountNote(a, l);
            if (differs) notes.push(differs);
            bind(a, l, 'gstin', 'strong', notes);
        }
    }

    // 2 and 3. The same tidied name, unique on both sides: strong only if the balance agrees too.
    {
        const byA = uniqueIndex(free(accounts, boundAccounts, a => a.id), a => tidyName(a.company) || null);
        const byL = uniqueIndex(free(ledgers, boundLedgers, l => l.sourceKey), l => tidyName(l.company) || null);
        for (const [name, a] of byA) {
            const l = byL.get(name);
            if (!l) continue;
            // Two different GSTINs are two businesses, whatever they are called; a GSTIN on several is for the owner.
            if (gstinUnsafe(a, l)) continue;
            const differs = amountNote(a, l);
            if (differs) bind(a, l, 'name_only', 'suggestion', ['Same name, but the balance differs.', differs]);
            else bind(a, l, 'name_and_amount', 'strong', ['Same name and the same balance.']);
        }
    }

    // 4. The same phone number or email, unique on both sides: a suggestion.
    for (const [label, keyA, keyL] of [
        ['phone number', (a: SwitchExisting) => tidyPhone(a.contactNumber), (l: SwitchIncoming) => tidyPhone(l.contact.contactNumber)],
        ['email', (a: SwitchExisting) => tidyEmail(a.email), (l: SwitchIncoming) => tidyEmail(l.contact.email)],
    ] as const) {
        const byA = uniqueIndex(free(accounts, boundAccounts, a => a.id), keyA);
        const byL = uniqueIndex(free(ledgers, boundLedgers, l => l.sourceKey), keyL);
        for (const [k, a] of byA) {
            const l = byL.get(k);
            if (!l) continue;
            if (gstinUnsafe(a, l)) continue;
            const differs = amountNote(a, l);
            bind(a, l, 'contact', 'suggestion', [`Same ${label}; named "${a.company}" in the book and "${l.company}" in TallyPrime.`, ...(differs ? [differs] : [])]);
        }
    }

    // What is left, and cannot be decided: conflicts, for the owner.
    const leftAccounts = free(accounts, boundAccounts, a => a.id);
    const leftLedgers = free(ledgers, boundLedgers, l => l.sourceKey);
    const conflicts: SwitchConflict[] = [];
    const group = <T>(items: T[], key: (t: T) => string | null) => {
        const out = new Map<string, T[]>();
        for (const item of items) { const k = key(item); if (k) out.set(k, [...(out.get(k) ?? []), item]); }
        return out;
    };
    const gstA = group(leftAccounts, a => tidyGstin(a.taxId));
    const gstL = group(leftLedgers, l => tidyGstin(l.contact.taxId));
    for (const [gstin, as] of gstA) {
        const ls = gstL.get(gstin) ?? [];
        if (ls.length && (as.length > 1 || ls.length > 1)) {
            conflicts.push({ reason: 'shared_gstin', accountIds: as.map(a => a.id), sourceKeys: ls.map(l => l.sourceKey),
                detail: `GSTIN ${gstin} is on ${as.length} account(s) here and ${ls.length} ledger(s) in TallyPrime.` });
        }
    }
    const nameA = group(leftAccounts, a => tidyName(a.company) || null);
    const nameL = group(leftLedgers, l => tidyName(l.company) || null);
    for (const [name, as] of nameA) {
        const ls = nameL.get(name) ?? [];
        if (!ls.length) continue;
        if (as.length > 1 || ls.length > 1) {
            conflicts.push({ reason: 'shared_name', accountIds: as.map(a => a.id), sourceKeys: ls.map(l => l.sourceKey),
                detail: `"${as[0]!.company}" is the name of ${as.length} account(s) here and ${ls.length} ledger(s) in TallyPrime.` });
        } else {
            const ga = tidyGstin(as[0]!.taxId);
            const gl = tidyGstin(ls[0]!.contact.taxId);
            if (ga && gl && ga !== gl) {
                conflicts.push({ reason: 'gstin_differs', accountIds: [as[0]!.id], sourceKeys: [ls[0]!.sourceKey],
                    detail: `"${as[0]!.company}" has GSTIN ${ga} here and ${gl} in TallyPrime: two businesses, or a mistake.` });
            }
        }
    }

    const unmatchedAccounts = leftAccounts
        .map(a => ({ accountId: a.id, accountName: a.company, total: a.total, totalType: a.totalType ?? 'Dr' }));
    const newLedgers = leftLedgers
        .map(l => ({ sourceKey: l.sourceKey, ledgerName: l.company, total: l.total, totalType: l.totalType }));
    const strong = bindings.filter(b => b.strength === 'strong').length;
    return {
        bindings, unmatchedAccounts, newLedgers, conflicts,
        counts: {
            accounts: accounts.length, ledgers: ledgers.length, strong, suggestions: bindings.length - strong,
            unmatchedAccounts: unmatchedAccounts.length, newLedgers: newLedgers.length, conflicts: conflicts.length,
        },
    };
}
